import React from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import AutoRequest from "./pages/AutoRequest";
import Login from "./pages/Login";
import Request from "./pages/Request";
import RequestMonitoring from "./pages/RequestMonitoring";
import Loading from "./pages/Loading";
import GetIdDemand from "./pages/GetIdDemand";
import GetIdDemandShow from "./pages/GetIdDemandShow";
import GetIdDemandMonitoring from "./pages/GetIdDemandMonitoring";
import RequestCreateMany from "./pages/RequestCreateMany";
import RequestCreate from "./pages/RequestCreate";
import Push from "./pages/Push";
import PushReprocess from "./pages/PushReprocess";
import PushReprocessMonitoring from "./pages/PushReprocessMonitoring";
import PushResendCallbacks from "./pages/PushResendCallbacks";
import PushResendCallbacksMonitoring from "./pages/PushResendCallbacksMonitoring";
import AutoRequestMonitoring from "./pages/AutoRequestMonitoring";
import ApiCallback from "./pages/ApiCallback";
import ApiCallbackMonitoring from "./pages/ApiCallbackMonitoring";
import Abrangencia from "./pages/Abrangencia";
import Reports from "./pages/Reports";
import DatateamReprocess from "./pages/DatateamReprocess";
import DatateamReprocessMonitoring from "./pages/DatateamReprocessMonitoring";
import DatateamVisualizar from "./pages/DatateamVisualizar";
import DatateamVisualizarMonitoring from "./pages/DatateamVisualizarMonitoring";
import DatateamNovo from "./pages/DatateamNovo";
import Consumos from "./pages/Consumos";
import IntegracoesSolucionare from "./pages/IntegracoesSolucionare";
import DashboardStatus from "./pages/DashboardStatus";
import DashboardApiConsulta from "./pages/DashboardApiConsulta";
import Ocorrencias from "./pages/Ocorrencias";
import PushDetails from "./pages/PushDetails";
import PushMonitoring from "./pages/PushMonitoring";
import PushVisualizar from "./pages/PushVisualizar";
import PushVisualizarMonitoring from "./pages/PushVisualizarMonitoring";
import PushAtrasados from "./pages/PushAtrasados";
import PushReverseEngineering from "./pages/PushReverseEngineering";
import UnderDevelopment from "./pages/UnderDevelopment";
import AutoRequestView from "./pages/AutoRequestView";
import AutoRequestViewMonitoring from "./pages/AutoRequestViewMonitoring";
import RequestCreateManyMonitoring from "./pages/RequestCreateManyMonitoring";
import AutorequestDetails from "./pages/AutorequestDetails";
import Dashboard from "./components/templates/Dashboard";

/**
 * Layout autenticado com menu lateral persistente entre navegações.
 */
const PrivateLayout: React.FC = () => (
  <Dashboard>
    <Outlet />
  </Dashboard>
);

/**
 * Componente para rotas privadas.
 * 
 * Verifica se o usuário está autenticado antes de permitir acesso.
 */
const PrivateRoute: React.FC = () => {
  const { token, hasAccess, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loading />;
  }

  if (!token || !hasAccess) {
    return (
      <Navigate
        to="/login"
        state={{ from: location, forbidden: Boolean(token) && !hasAccess }}
        replace
      />
    );
  }

  return <Outlet />;
};

/**
 * Componente de roteamento da aplicação.
 * 
 * Define todas as rotas da aplicação, incluindo rotas públicas e privadas.
 */
const Router: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<PrivateRoute />}>
          <Route element={<PrivateLayout />}>
          {/* Dashboards — rota inicial pós-login */}
          <Route index element={<Navigate to="/dashboard/status" replace />} />
          <Route path="/dashboard" element={<Navigate to="/dashboard/status" replace />} />
          <Route path="/dashboard/status" element={<DashboardStatus />} />
          <Route path="/dashboard/api-consulta" element={<DashboardApiConsulta />} />
          {/* CapturaWeb - Request (CW1) */}
          <Route path="/request" element={<Request />} />
          <Route path="/request/create" element={<RequestCreate />} />
          <Route path="/request/create-many" element={<RequestCreateMany />} />
          <Route path="/request/create-many/monitoring" element={<RequestCreateManyMonitoring />} />
          <Route path="/request/monitoring" element={<RequestMonitoring />} />
          
          {/* CapturaWeb - Auto Request (CW2) */}
          <Route path="/auto-request" element={<AutoRequest />} />
          <Route path="/auto-request/monitoring" element={<AutoRequestMonitoring />} />
          <Route path="/auto-request/buscar" element={<AutoRequestView />} />
          <Route path="/auto-request/buscar/monitoring" element={<AutoRequestViewMonitoring />} />
          
          {/* Buscar Fonte */}
          <Route path="/buscar-fonte" element={<GetIdDemand />} />
          <Route path="/buscar-fonte/monitoring" element={<GetIdDemandMonitoring />} />
          <Route path="/buscar-fonte/:id" element={<GetIdDemandShow />} />
          
          {/* Push */}
          <Route path="/push" element={<Push />} />
          <Route path="/push/monitoring" element={<PushMonitoring />} />
          <Route path="/push/visualizar" element={<PushVisualizar />} />
          <Route path="/push/visualizar/monitoring" element={<PushVisualizarMonitoring />} />
          <Route path="/push/reprocessar" element={<PushReprocess />} />
          <Route path="/push/reprocessar/monitoring" element={<PushReprocessMonitoring />} />
          <Route path="/push/reenviar-callbacks" element={<PushResendCallbacks />} />
          <Route path="/push/reenviar-callbacks/monitoring" element={<PushResendCallbacksMonitoring />} />
          <Route path="/push/atrasados" element={<PushAtrasados />} />
          <Route path="/push/buscar-id" element={<PushReverseEngineering />} />
          <Route path="/push-details/:id" element={<PushDetails />} />
          
          {/* Autorequest Details */}
          <Route path="/autorequest-details/:id" element={<AutorequestDetails />} />
          
          {/* API Callback */}
          <Route path="/api-callback" element={<ApiCallback />} />
          <Route path="/api-callback/monitoring" element={<ApiCallbackMonitoring />} />
          
          {/* Abrangência */}
          <Route path="/abrangencia" element={<Abrangencia />} />

          {/* Status - Ocorrências */}
          <Route path="/status/ocorrencias" element={<Ocorrencias />} />
          
          {/* DataTeam */}
          <Route path="/datateam" element={<UnderDevelopment />} />
          <Route path="/datateam/novo" element={<DatateamNovo />} />
          <Route path="/datateam/visualizar" element={<DatateamVisualizar />} />
          <Route path="/datateam/visualizar/monitoring" element={<DatateamVisualizarMonitoring />} />
          <Route path="/datateam/reprocessar" element={<DatateamReprocess />} />
          <Route path="/datateam/reprocessar/monitoring" element={<DatateamReprocessMonitoring />} />
          
          {/* Relatórios */}
          <Route path="/reports" element={<Reports />} />
          
          {/* Integrações - Solucionare */}
          <Route path="/integrations/solucionare" element={<IntegracoesSolucionare />} />

          {/* Consumos */}
          <Route path="/consumos" element={<Consumos />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/dashboard/status" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Router;
